import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Typography, Alert,
  FormControlLabel, Checkbox
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { uploadSingleImage, uploadVideo } from '../../services/upload.service';

/**
 * Convert YouTube watch URL to embed URL
 */
function normalizeVideoUrl(url: string): string {
  if (!url) return url;
  
  // Already a valid URL with protocol
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // YouTube watch URL → embed URL
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo URL → embed URL
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  }
  
  // Add https if missing
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  
  // Assume it's a relative path or domain without protocol
  return `https://${url}`;
}

const videosSchema = z.object({
  title: z.string().min(1, 'Укажите название'),
  videoUrl: z.string().min(1, 'Укажите URL видео'), // Uploaded via DropZone or external URL
  thumbnailUrl: z.string().optional(), // Uploaded via DropZone
  duration: z.string().optional(),
  category: z.string().optional(),
  description: z.string().optional(),
  isPublic: z.boolean().default(false),
});

type VideosFormValues = z.infer<typeof videosSchema>;

interface VideosFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VideosFormValues) => Promise<void>;
  initialData?: VideosFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function VideosForm({ open, onClose, onSubmit, initialData, loading, error }: VideosFormProps) {
  const [videoUploading, setVideoUploading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset,
    setValue,
    watch,
  } = useForm<VideosFormValues>({
    resolver: zodResolver(videosSchema),
    defaultValues: { 
      title: '', 
      videoUrl: '', 
      thumbnailUrl: '', 
      duration: '', 
      category: '', 
      description: '', 
      isPublic: false 
    },
  });

  const videoUrl = watch('videoUrl');
  const thumbnailUrl = watch('thumbnailUrl');

  useEffect(() => {
    if (initialData) reset(initialData);
    else reset({ 
      title: '', 
      videoUrl: '', 
      thumbnailUrl: '', 
      duration: '', 
      category: '', 
      description: '', 
      isPublic: false 
    });
  }, [initialData, open, reset]);

  // Handle video upload via DropZone
  const handleVideoUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setVideoUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadVideo(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setVideoUploading(false);
    }
  }, []);

  const handleVideoChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('videoUrl', url);
  }, [setValue]);

  // Handle thumbnail upload via DropZone
  const handleThumbnailUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setThumbnailUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setThumbnailUploading(false);
    }
  }, []);

  const handleThumbnailChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('thumbnailUrl', url);
  }, [setValue]);

  // Handle form submit with URL normalization
  const handleFormSubmit = useCallback(async (data: VideosFormValues) => {
    const normalizedData = {
      ...data,
      videoUrl: normalizeVideoUrl(data.videoUrl),
    };
    await onSubmit(normalizedData);
  }, [onSubmit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать видео' : 'Добавить видео'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Название *" 
                error={!!errors.title} 
                helperText={errors.title?.message} 
                {...register('title')} 
              />
            </Grid>
            
            {/* Video Upload — DropZone */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Видеофайл
              </Typography>
              <DropZone
                onUpload={handleVideoUpload}
                type="video"
                maxFiles={1}
                maxSize={50}
                multiple={false}
                value={videoUrl || ''}
                onChange={handleVideoChange}
                label="Перетащите видео или нажмите для выбора"
                helperText="MP4, WebM до 50MB"
              />
              {videoUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка видео...
                </Typography>
              )}
              {/* Alternative: External URL */}
              <TextField
                fullWidth
                label="или укажите URL видео (YouTube, Vimeo)"
                error={!!errors.videoUrl}
                helperText={errors.videoUrl?.message || 'Можно загрузить файл или указать внешнюю ссылку'}
                {...register('videoUrl')}
                sx={{ mt: 2 }}
              />
            </Grid>
            
            {/* Thumbnail Upload — DropZone */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Превью (обложка видео)
              </Typography>
              <DropZone
                onUpload={handleThumbnailUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={thumbnailUrl || ''}
                onChange={handleThumbnailChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {thumbnailUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Duration */}
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                label="Длительность" 
                placeholder="например: 10:30"
                {...register('duration')} 
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                label="Категория" 
                {...register('category')} 
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Описание" 
                multiline 
                rows={3} 
                {...register('description')} 
              />
            </Grid>
            
            {/* isPublic */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('isPublic')} />}
                label="Видео доступно публично"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button 
          onClick={handleSubmit(handleFormSubmit)} 
          variant="contained" 
          disabled={loading || videoUploading || thumbnailUploading}
        >
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}